import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./local-storage.service";

export interface ActionCableMessage {
  identifier: string;
  message?: any;
  type?: string;
}

export interface ActionCableCommand {
  command: string;
  identifier: string;
}

export interface WebSocketConnectionState {
  connected: boolean;
  connecting: boolean;
  error?: string;
}

@Injectable({
  providedIn: "root",
})
export class WebSocketService {
  private ws: WebSocket | null = null;
  private connectionState$ = new BehaviorSubject<WebSocketConnectionState>({
    connected: false,
    connecting: false,
  });
  private messageSubject$ = new Subject<ActionCableMessage>();
  private subscriptions = new Map<string, boolean>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor(private localStorageService: LocalStorageService) {}

  /**
   * Conecta ao WebSocket ActionCable
   */
  connect(): Observable<WebSocketConnectionState> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return this.connectionState$.asObservable();
    }

    this.connectionState$.next({ connected: false, connecting: true });
    const wsUrl = this.getWebSocketUrl();
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log("WebSocket conectado");
      this.reconnectAttempts = 0;
      this.connectionState$.next({ connected: true, connecting: false });
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onclose = (event) => {
      console.log("WebSocket desconectado:", event.code, event.reason);
      this.connectionState$.next({ connected: false, connecting: false });
      this.handleReconnection();
    };

    this.ws.onerror = (error) => {
      console.error("Erro no WebSocket:", error);
      this.connectionState$.next({
        connected: false,
        connecting: false,
        error: "Erro de conexão",
      });
    };

    return this.connectionState$.asObservable();
  }

  /**
   * Desconecta do WebSocket
   */
  disconnect(): void {
    this.subscriptions.clear();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connectionState$.next({ connected: false, connecting: false });
  }

  /**
   * Subscreve a um canal ActionCable
   */
  subscribe(channel: string, params: any = {}): Observable<any> {
    const identifier = JSON.stringify({ channel, ...params });

    if (this.subscriptions.has(identifier)) {
      console.log("📡 Já subscrevido ao canal:", channel);
      return this.messageSubject$.asObservable();
    }

    const command: ActionCableCommand = {
      command: "subscribe",
      identifier,
    };

    console.log("📡 Subscrevendo ao canal ActionCable:", channel, params);
    this.sendCommand(command);
    this.subscriptions.set(identifier, true);

    return this.messageSubject$.asObservable();
  }

  /**
   * Cancela subscrição de um canal
   */
  unsubscribe(channel: string, params: any = {}): void {
    const identifier = JSON.stringify({ channel, ...params });

    const command: ActionCableCommand = {
      command: "unsubscribe",
      identifier,
    };

    console.log(
      "📡 Cancelando subscrição do canal ActionCable:",
      channel,
      params,
    );
    this.sendCommand(command);
    this.subscriptions.delete(identifier);
  }

  /**
   * Obtém o estado da conexão
   */
  getConnectionState(): Observable<WebSocketConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Verifica se está conectado
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private getWebSocketUrl(): string {
    // Usar a URL base do ambiente ou fallback para localhost
    const baseUrl = environment.API_BACKEND || "http://localhost:3000";
    const wsUrl = baseUrl.replace(/^http/, "ws");

    // Obter token de autenticação
    const token = this.localStorageService.getItem("auth-token");

    if (token) {
      return `${wsUrl}/cable?token=${encodeURIComponent(token)}`;
    } else {
      console.warn("⚠️ Token de autenticação não encontrado para WebSocket");
      return `${wsUrl}/cable`;
    }
  }

  private sendCommand(command: ActionCableCommand): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(command));
    } else {
      console.warn(
        "WebSocket não está conectado. Comando não enviado:",
        command,
      );
    }
  }

  private handleMessage(data: string): void {
    try {
      const message: ActionCableMessage = JSON.parse(data);

      // Processar apenas mensagens com conteúdo real
      if (message.message) {
        this.messageSubject$.next(message);
      }

      // Log apenas mensagens importantes (não pings)
      if (message.type !== "ping") {
        console.log("📨 Mensagem ActionCable recebida:", message);
      }
    } catch (error) {
      console.error("Erro ao processar mensagem WebSocket:", error);
    }
  }

  private handleReconnection(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay =
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

      console.log(
        `Tentando reconectar em ${delay}ms (tentativa ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
      );

      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error("Máximo de tentativas de reconexão atingido");
      this.connectionState$.next({
        connected: false,
        connecting: false,
        error: "Falha na reconexão",
      });
    }
  }
}
